/**
 * Embed App
 * Main application shell for the embedded app
 *
 * Architecture:
 * - EmbedConfigProvider: Provides API URLs and basic config
 * - EmbedThemeProvider: Provides centralized theme system
 * - EmbedAuthProvider: Manages authentication state
 * - EmbedOwnerProfileProvider: Provides owner profile data
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AgentSelectorDropdown } from '../components/AgentSelectorDropdown';
import { debugLog } from '../debug';
import { EmbedBrainPage } from '../pages/EmbedBrainPage';
import { EmbedLoginPage } from '../pages/EmbedLoginPage';
import { EmbedWidgetEditorPage } from '../pages/EmbedWidgetEditorPage';
import { EmbedAuthProvider, useEmbedAuth } from '../providers/EmbedAuthProvider';
import { EmbedChatActionsProvider } from '../providers/EmbedChatActions';
import { EmbedConfigProvider, useEmbedConfig } from '../providers/EmbedConfigProvider';
import { EmbedOwnerProfileProvider } from '../providers/EmbedOwnerProfileProvider';
import { EmbedThemeConfig, EmbedThemeProvider, useEmbedTheme } from '../theme';
import type { AdvancedWidgetConfig } from '../theme/types';

// ============================================================================
// Path Routing Helper
// ============================================================================

interface ParsedRoute {
  type: 'default' | 'brain' | 'editor' | 'unknown';
  params: Record<string, string>;
  searchParams: URLSearchParams;
}

/**
 * Validates that a path segment contains only safe characters.
 * Prevents path traversal and injection attacks.
 */
function isValidPathSegment(segment: string): boolean {
  // Only allow alphanumeric, underscore, hyphen, and dot
  // Must not start with dot (prevents hidden files)
  // Must not contain path traversal sequences
  if (!segment || segment.length > 128) return false;
  if (segment.startsWith('.')) return false;
  if (segment.includes('..')) return false;
  return /^[a-zA-Z0-9_-]+(\.[a-zA-Z0-9_-]+)?$/.test(segment);
}

/**
 * Sanitizes URL search params to prevent injection.
 */
function sanitizeSearchParams(queryString: string): URLSearchParams {
  const params = new URLSearchParams(queryString || '');
  const sanitized = new URLSearchParams();

  // Only allow specific known parameters
  const allowedParams = ['pov', 'tab', 'view'];

  params.forEach((value, key) => {
    if (allowedParams.includes(key.toLowerCase())) {
      // Validate the value contains only safe characters
      if (/^[a-zA-Z0-9_-]+$/.test(value)) {
        sanitized.set(key, value);
      }
    }
  });

  return sanitized;
}

function parsePath(path?: string): ParsedRoute {
  if (!path) {
    return { type: 'default', params: {}, searchParams: new URLSearchParams() };
  }

  // Security: Reject paths with protocol or external URLs
  if (path.includes('://') || path.startsWith('//')) {
    return { type: 'unknown', params: {}, searchParams: new URLSearchParams() };
  }

  // Security: Reject paths with path traversal
  if (path.includes('..')) {
    return { type: 'unknown', params: {}, searchParams: new URLSearchParams() };
  }

  // Split path and query string
  const [pathname, queryString] = path.split('?');
  const searchParams = sanitizeSearchParams(queryString || '');

  // Match /editor (widget editor)
  if (pathname === '/editor') {
    return { type: 'editor', params: {}, searchParams };
  }

  // Match /brain/:id
  const brainMatch = pathname.match(/^\/brain\/([^/]+)$/);
  if (brainMatch) {
    const id = brainMatch[1];
    // Validate brain ID format (MongoDB ObjectId or similar)
    if (isValidPathSegment(id) && /^[a-f0-9]{24}$/i.test(id)) {
      return { type: 'brain', params: { id }, searchParams };
    }
    return { type: 'unknown', params: {}, searchParams };
  }

  return { type: 'unknown', params: {}, searchParams };
}

// ============================================================================
// Session Expired Notification Component
// ============================================================================

interface SessionExpiredNotificationProps {
  onDismiss: () => void;
}

const SessionExpiredNotification: React.FC<SessionExpiredNotificationProps> = ({ onDismiss }) => {
  const { theme, styles, utils } = useEmbedTheme();

  return (
    <div
      style={utils.merge(styles.flex({ align: 'center', gap: '3' }), {
        position: 'absolute',
        top: theme.spacing['3'],
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1000,
        padding: `${theme.spacing['3']} ${theme.spacing['4']}`,
        backgroundColor: theme.colors.background.surface,
        borderRadius: theme.borderRadius.lg,
        boxShadow: theme.shadow.lg,
        border: `${theme.borderWidth.thin} solid ${theme.colors.border.subtle}`,
        maxWidth: '90%',
        animation: 'embed-slide-down 0.3s ease-out',
      })}
    >
      {/* Warning Icon */}
      <div
        style={{
          width: '32px',
          height: '32px',
          borderRadius: theme.borderRadius.full,
          backgroundColor: '#fef3c7',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      </div>

      {/* Message */}
      <div style={{ flex: 1 }}>
        <p style={styles.text({ variant: 'primary', size: 'base' })}>
          <strong>Session Expired</strong>
        </p>
        <p style={utils.merge(styles.text({ variant: 'secondary', size: 'sm' }), { marginTop: '2px' })}>
          Please sign in again to continue.
        </p>
      </div>

      {/* Dismiss Button */}
      <button onClick={onDismiss} style={styles.primaryButton({ size: 'sm' })}>
        OK
      </button>
    </div>
  );
};

// ============================================================================
// Types
// ============================================================================

export interface EmbedAppProps {
  // API Configuration
  graphqlUrl?: string;
  apiBaseUrl?: string;
  appBaseUrl?: string;
  mediaBaseUrl?: string;
  assetApiUrl?: string;

  // Theme (basic)
  theme?: 'light' | 'dark';
  primaryColor?: string;
  title?: string;

  // Theme (advanced configuration)
  themeConfig?: EmbedThemeConfig;

  // Owner info
  id?: string;
  ownerHandle?: string;

  // Routing
  path?: string;

  // Callbacks
  onReady?: () => void;
  onEvent?: (eventName: string, data: unknown) => void;
  /** Callback when editor config changes (for WordPress integration) */
  onConfigChange?: (config: any) => void;
  /** Callback when authentication state changes */
  onAuthChange?: (isAuthenticated: boolean) => void;

  // Container styling
  className?: string;
  style?: React.CSSProperties;

  /** Guest mode - skip login prompts and allow guest interactions */
  guestMode?: boolean;

  /** Initial message to send automatically when the widget loads */
  initialMessage?: string;

  /** Agent icon style for AI messages */
  agentIcon?: 'robot' | 'sparkle' | 'brain' | 'chat' | 'message';

  /** Force showing the login page regardless of authentication state */
  forceLogin?: boolean;

  /** Advanced widget configuration for fine-grained visual and behavioral customization */
  advancedConfig?: AdvancedWidgetConfig;

  /** Custom conversation starters (pills) — overrides the brain's launch prompts when set */
  conversationStarters?: string[];

  /** Chat bubble style: bubbles (default), minimal, or cards */
  chatStyle?: 'bubbles' | 'minimal' | 'cards';
  /** Header: override the brain's display name */
  displayNameOverride?: string;
  /** Header: subtitle line shown under the name */
  headerSubtitle?: string;
  /** Header: override the brain's display image (URL) */
  displayImageOverride?: string;

  /** Available agents (brains) for the dropdown selector */
  agents?: Array<{
    type: 'brain';
    id: string;
    name: string;
    displayImage?: string;
  }>;

  /** Currently selected agent */
  selectedAgent?: {
    type: 'brain';
    id: string;
    name: string;
    displayImage?: string;
  } | null;

  /** Callback when agent is changed via dropdown */
  onAgentChange?: (agent: { type: 'brain'; id: string; name: string; displayImage?: string }) => void;

  /** Show configure button that opens /distribute in a new tab (for external embeds) */
  showConfigureButton?: boolean;

  /** Callback when collapse button is clicked (shows collapse button in header when provided) */
  onCollapse?: () => void;

  /** Headless mode - hide header/agent selector for embedding within another UI that provides its own */
  headless?: boolean;
}

// ============================================================================
// Placeholder Component (for both authenticated users and guests without content)
// ============================================================================

interface PlaceholderProps {
  isGuest?: boolean;
}

const ContentPlaceholder: React.FC<PlaceholderProps> = ({ isGuest = false }) => {
  const { theme, styles, utils, primaryColor } = useEmbedTheme();
  const { user, logout } = useEmbedAuth();
  const { appBaseUrl } = useEmbedConfig();

  if (isGuest) {
    // Guest mode - no content configured
    return (
      <div
        style={utils.merge(styles.flex({ direction: 'column', align: 'center', justify: 'center' }), {
          height: '100%',
          backgroundColor: theme.colors.background.subtle,
          padding: theme.spacing['6'],
          textAlign: 'center',
        })}
      >
        <div
          style={utils.merge(styles.avatar({ size: 'xl' }), {
            backgroundColor: theme.colors.palette.primaryLight,
            marginBottom: theme.spacing['6'],
            color: primaryColor,
          })}
        >
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </div>

        <h2 style={utils.merge(styles.heading({ level: 2 }), { marginBottom: theme.spacing['3'] })}>
          Widget Not Configured
        </h2>

        <p
          style={utils.merge(styles.text({ variant: 'secondary' }), {
            marginBottom: theme.spacing['4'],
            maxWidth: '280px',
          })}
        >
          This widget needs to be configured with a Brain or POV to display content.
        </p>

        {appBaseUrl && (
          <a
            href={`${appBaseUrl}/distribute`}
            target="_blank"
            rel="noopener noreferrer"
            style={utils.merge(styles.primaryButton({ size: 'md' }), {
              textDecoration: 'none',
            })}
          >
            Configure Widget
          </a>
        )}
      </div>
    );
  }

  // Authenticated user
  return (
    <div
      style={utils.merge(styles.flex({ direction: 'column' }), {
        height: '100%',
        backgroundColor: theme.colors.background.subtle,
      })}
    >
      {/* Header */}
      <div style={styles.header()}>
        <div style={styles.flex({ align: 'center', justify: 'between' })}>
          <h1 style={styles.heading({ level: 3 })}>Welcome</h1>
          <button onClick={() => logout()} style={styles.secondaryButton({ size: 'sm' })}>
            Logout
          </button>
        </div>
      </div>

      {/* Content */}
      <div
        style={utils.merge(styles.flex({ direction: 'column', align: 'center', justify: 'center' }), {
          flex: 1,
          padding: theme.spacing['6'],
          textAlign: 'center',
        })}
      >
        <div
          style={utils.merge(styles.avatar({ size: 'xl' }), {
            backgroundColor: theme.colors.palette.primaryLight,
            marginBottom: theme.spacing['6'],
            color: primaryColor,
          })}
        >
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
        </div>

        <h2 style={utils.merge(styles.heading({ level: 2 }), { marginBottom: theme.spacing['2'] })}>
          {user?.fullName || user?.email || 'User'}
        </h2>

        <p style={utils.merge(styles.text({ variant: 'secondary' }), { marginBottom: theme.spacing['6'] })}>
          {user?.email}
        </p>

        <div style={styles.card()}>
          <p style={styles.text({ variant: 'secondary' })}>You are logged in successfully.</p>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// App Content (with auth check and routing)
// ============================================================================

// Refresh Icon SVG
const RefreshIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
    <path d="M21 3v5h-5" />
    <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
    <path d="M8 16H3v5" />
  </svg>
);

// New chat (square-pen)
const NewChatIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z" />
  </svg>
);

// User Icon SVG
const UserIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

// Settings/Gear Icon SVG
const SettingsIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const CollapseIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

interface AppContentProps {
  wantsToSignIn: boolean;
  onCancelSignIn: () => void;
}

const AppContent: React.FC<AppContentProps> = ({ wantsToSignIn, onCancelSignIn }) => {
  const { isAuthenticated, isLoading } = useEmbedAuth();
  const { path, forceLogin, guestMode } = useEmbedConfig();
  const { theme, styles, utils } = useEmbedTheme();

  // Track if user dismissed the login by clicking "Continue as Guest"
  const [dismissedLogin, setDismissedLogin] = useState(false);

  // Reset dismissed state when forceLogin changes (e.g., user clicks Sign in again)
  useEffect(() => {
    if (forceLogin) {
      setDismissedLogin(false);
    }
  }, [forceLogin]);

  // Handle Continue as Guest - works for both forced login and default login
  const handleContinueAsGuest = useCallback(() => {
    debugLog('[AppContent] Continue as Guest clicked - dismissing login');
    setDismissedLogin(true);
  }, []);

  // Debug logging
  debugLog('[AppContent] ========== RENDER ==========');
  debugLog('[AppContent] forceLogin from config:', forceLogin);
  debugLog('[AppContent] isAuthenticated:', isAuthenticated);
  debugLog('[AppContent] isLoading:', isLoading);
  debugLog('[AppContent] path:', path);
  debugLog('[AppContent] dismissedLogin:', dismissedLogin);

  // Parse the path to determine which page to show
  const route = useMemo(() => parsePath(path), [path]);

  // Render content based on state
  const renderContent = () => {
    // User clicked sign in button - show login page
    if (wantsToSignIn && !isAuthenticated) {
      debugLog('[AppContent] wantsToSignIn is TRUE - showing EmbedLoginPage');
      return <EmbedLoginPage onContinueAsGuest={onCancelSignIn} />;
    }

    // Force login takes HIGHEST priority - show login page immediately without waiting for auth
    // This bypasses the loading state to avoid delay when user clicks "Sign in"
    // BUT: If user is already authenticated, don't force login (they just logged in successfully)
    // Also skip if user dismissed by clicking "Continue as Guest"
    if (forceLogin && !isAuthenticated && !dismissedLogin) {
      debugLog('[AppContent] forceLogin is TRUE and not authenticated - showing EmbedLoginPage immediately');
      return <EmbedLoginPage onContinueAsGuest={handleContinueAsGuest} />;
    }

    // Show loading state (only if not forcing login)
    if (isLoading) {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            width: '100%',
            backgroundColor: theme.colors.background.subtle,
          }}
        >
          <div style={styles.spinner()} />
        </div>
      );
    }

    debugLog('[AppContent] forceLogin is FALSE or undefined, continuing with route:', route.type);

    // If path is set, show the specific page (may or may not require auth)
    if (route.type === 'editor') {
      // Editor always requires authentication - handled inside the component
      return <EmbedWidgetEditorPage />;
    }

    if (route.type === 'brain') {
      // Key ensures component remounts when brainId changes
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            width: '100%',
            minHeight: 0,
            position: 'relative',
          }}
        >
          <EmbedBrainPage key={route.params.id} brainId={route.params.id} />
        </div>
      );
    }

    // Default flow: Show login if not authenticated (unless dismissed or guest mode with dismissed)
    if (!isAuthenticated && !dismissedLogin) {
      return <EmbedLoginPage onContinueAsGuest={handleContinueAsGuest} />;
    }

    // Show placeholder - for guests (not authenticated but dismissed login) or authenticated users
    return <ContentPlaceholder isGuest={!isAuthenticated} />;
  };

  return renderContent();
};

// ============================================================================
// Main App Component
// ============================================================================

export const EmbedApp: React.FC<EmbedAppProps> = ({
  graphqlUrl = '',
  apiBaseUrl,
  appBaseUrl,
  mediaBaseUrl,
  assetApiUrl,
  theme = 'light',
  primaryColor = '#3b82f6',
  title = 'Bolder Space',
  themeConfig,
  id,
  ownerHandle,
  path,
  onReady,
  onEvent,
  onConfigChange,
  className = '',
  style,
  guestMode = false,
  initialMessage,
  agentIcon = 'message',
  forceLogin = false,
  onAuthChange,
  agents = [],
  selectedAgent = null,
  onAgentChange,
  showConfigureButton = false,
  onCollapse,
  advancedConfig,
  conversationStarters,
  chatStyle,
  displayNameOverride,
  headerSubtitle,
  displayImageOverride,
  headless = false,
}) => {
  // Session expiry notification state
  const [showSessionExpired, setShowSessionExpired] = useState(false);

  // Handle session expiry from auth provider
  const handleSessionExpired = useCallback(() => {
    setShowSessionExpired(true);
    onEvent?.('auth:session_expired', { timestamp: Date.now() });

    // Auto-dismiss after 10 seconds
    setTimeout(() => {
      setShowSessionExpired(false);
    }, 10000);
  }, [onEvent]);

  // Dismiss session expired notification
  const handleDismissSessionExpired = useCallback(() => {
    setShowSessionExpired(false);
  }, []);

  // Emit ready event
  useEffect(() => {
    onReady?.();
    onEvent?.('app:ready', { theme, graphqlUrl });
  }, []);

  // Merge basic theme props with advanced themeConfig
  const mergedThemeConfig = useMemo<EmbedThemeConfig>(
    () => ({
      mode: theme,
      primaryColor,
      ...themeConfig,
    }),
    [theme, primaryColor, themeConfig],
  );

  return (
    <EmbedConfigProvider
      config={{
        graphqlUrl,
        apiBaseUrl,
        appBaseUrl,
        mediaBaseUrl,
        assetApiUrl,
        theme,
        primaryColor,
        title,
        themeConfig: mergedThemeConfig,
        id,
        ownerHandle,
        path,
        onEvent,
        onConfigChange,
        guestMode,
        initialMessage,
        agentIcon,
        forceLogin,
        agents,
        selectedAgent,
        onAgentChange,
        showConfigureButton,
        onCollapse,
        advancedConfig,
        conversationStarters,
        chatStyle,
        displayNameOverride,
        headerSubtitle,
        displayImageOverride,
        headless,
      }}
    >
      <EmbedThemeProvider config={mergedThemeConfig}>
        <ThemedAppContainer
          className={className}
          style={style}
          onSessionExpired={handleSessionExpired}
          onAuthChange={onAuthChange}
        >
          {showSessionExpired && <SessionExpiredNotification onDismiss={handleDismissSessionExpired} />}
        </ThemedAppContainer>
      </EmbedThemeProvider>
    </EmbedConfigProvider>
  );
};

// ============================================================================
// Main Layout (Header + AppContent)
// ============================================================================

const MainLayout: React.FC = () => {
  const { isAuthenticated, logout, user } = useEmbedAuth();
  const {
    agents,
    selectedAgent,
    onAgentChange,
    mediaBaseUrl,
    guestMode,
    showConfigureButton,
    appBaseUrl,
    onCollapse,
    headless,
    displayNameOverride,
    headerSubtitle,
    displayImageOverride,
  } = useEmbedConfig();
  const { theme } = useEmbedTheme();

  // Key to force remount of pages (for new chat functionality)
  const [contentKey, setContentKey] = useState(0);
  // The conversation page's own reset, offered while it has a conversation.
  const [newChat, setNewChat] = useState<(() => void) | null>(null);
  const offerNewChat = useCallback((next: (() => void) | null) => setNewChat(() => next), []);
  // Track if user wants to sign in (clicked sign in button)
  const [wantsToSignIn, setWantsToSignIn] = useState(false);
  // Track if user card popup is shown
  const [showUserCard, setShowUserCard] = useState(false);
  // Ref for user card container (click outside detection)
  const userCardRef = useRef<HTMLDivElement>(null);

  // Close user card when clicking outside
  useEffect(() => {
    if (!showUserCard) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (userCardRef.current && !userCardRef.current.contains(event.target as Node)) {
        setShowUserCard(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserCard]);

  // Reset wantsToSignIn when user successfully authenticates
  useEffect(() => {
    if (isAuthenticated && wantsToSignIn) {
      setWantsToSignIn(false);
    }
  }, [isAuthenticated, wantsToSignIn]);

  // Handle new chat - reset content
  const handleNewChat = useCallback(() => {
    setContentKey((prev) => prev + 1);
    setWantsToSignIn(false);
  }, []);

  // Handle sign in button click
  const handleSignInClick = useCallback(() => {
    if (isAuthenticated) {
      // Already authenticated - toggle user card
      setShowUserCard((prev) => !prev);
    } else {
      // Not authenticated - show login
      setWantsToSignIn(true);
    }
  }, [isAuthenticated]);

  // Handle logout from user card
  const handleLogout = useCallback(() => {
    setShowUserCard(false);
    logout();
    setContentKey((prev) => prev + 1);
  }, [logout]);

  // Handle cancel sign in
  const handleCancelSignIn = useCallback(() => {
    setWantsToSignIn(false);
  }, []);

  // Handle configure button click
  const handleConfigureClick = useCallback(() => {
    const distributeUrl = `${appBaseUrl}/distribute`;
    window.open(distributeUrl, '_blank', 'noopener,noreferrer');
  }, [appBaseUrl]);

  // Determine if we should show the persistent header
  // Show if: NOT headless AND (agents configured OR collapse button needed OR configure button requested)
  const showPersistentHeader =
    !headless && ((agents && agents.length > 0) || !!onCollapse || showConfigureButton);

  // Minimal FloatingAssistant-style header: brain identity on the left, only a
  // minimize/close on the right. The configure / new-chat / sign-in actions are
  // intentionally not rendered in the header to match the on-site assistant.
  const showHeaderActions = false;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        width: '100%',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      {/* Main Header Bar */}
      {showPersistentHeader && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 12px',
            backgroundColor: theme.colors.background.surface,
            minHeight: '48px',
            flexShrink: 0,
          }}
        >
          {/* Left: Brain identity (avatar + name + subtitle) — matches FloatingAssistant */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flex: 1 }}>
            {(() => {
              const headerAgent = selectedAgent || agents?.[0];
              const name = displayNameOverride || headerAgent?.name || 'AI Assistant';
              const rawImg = displayImageOverride || headerAgent?.displayImage;
              const img = rawImg
                ? mediaBaseUrl && !/^https?:\/\//.test(rawImg)
                  ? `${mediaBaseUrl}${rawImg}`
                  : rawImg
                : '';
              const subtitle = headerSubtitle || headerAgent?.description;
              return (
                <>
                  <div
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: theme.borderRadius.md,
                      overflow: 'hidden',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: img ? undefined : theme.colors.palette.primary,
                      color: theme.colors.text.inverse,
                      fontWeight: theme.typography.fontWeight.semibold,
                      fontSize: theme.typography.fontSize.sm,
                    }}
                  >
                    {img ? (
                      <img
                        src={img}
                        alt={name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      name.charAt(0).toUpperCase()
                    )}
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        fontSize: theme.typography.fontSize.sm,
                        fontWeight: theme.typography.fontWeight.semibold,
                        color: theme.colors.text.primary,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {name}
                    </div>
                    {subtitle && (
                      <div
                        style={{
                          fontSize: '11px',
                          color: theme.colors.text.secondary,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {subtitle}
                      </div>
                    )}
                  </div>
                </>
              );
            })()}
          </div>

          {/* Right: Configure + New Chat + Sign In/Out + Collapse */}
          <div
            style={{
              display: 'flex',
              gap: '6px',
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            {showHeaderActions && (
              <>
                {/* Configure Button */}
                {showConfigureButton && (
                  <button
                    onClick={handleConfigureClick}
                    title="Configure widget"
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: theme.borderRadius.md,
                      border: `${theme.borderWidth.thin} solid ${theme.colors.border.subtle}`,
                      backgroundColor: theme.colors.background.surface,
                      color: theme.colors.text.secondary,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: theme.shadow.sm,
                      transition: 'all 0.15s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = theme.colors.background.hover;
                      e.currentTarget.style.borderColor = theme.colors.border.hover;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = theme.colors.background.surface;
                      e.currentTarget.style.borderColor = theme.colors.border.subtle;
                    }}
                  >
                    <SettingsIcon />
                  </button>
                )}

                {/* New Chat Button */}
                <button
                  onClick={handleNewChat}
                  title="New conversation"
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: theme.borderRadius.md,
                    border: `${theme.borderWidth.thin} solid ${theme.colors.border.subtle}`,
                    backgroundColor: theme.colors.background.surface,
                    color: theme.colors.text.secondary,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: theme.shadow.sm,
                    transition: 'all 0.15s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = theme.colors.background.hover;
                    e.currentTarget.style.borderColor = theme.colors.border.hover;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = theme.colors.background.surface;
                    e.currentTarget.style.borderColor = theme.colors.border.subtle;
                  }}
                >
                  <RefreshIcon />
                </button>

                {/* Sign In/Out Button - hidden in pure guest mode */}
                {!guestMode && (
                  <div ref={userCardRef} style={{ position: 'relative' }}>
                    <button
                      onClick={handleSignInClick}
                      title={isAuthenticated ? 'Account' : 'Sign in'}
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: theme.borderRadius.md,
                        border: `${theme.borderWidth.base} solid ${
                          isAuthenticated ? theme.colors.border.hover : theme.colors.border.subtle
                        }`,
                        backgroundColor: isAuthenticated
                          ? theme.colors.background.hover
                          : theme.colors.background.surface,
                        color: isAuthenticated ? theme.colors.text.primary : theme.colors.text.secondary,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: theme.shadow.sm,
                        transition: 'all 0.15s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = theme.colors.background.hover;
                        e.currentTarget.style.borderColor = theme.colors.border.hover;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = isAuthenticated
                          ? theme.colors.background.hover
                          : theme.colors.background.surface;
                        e.currentTarget.style.borderColor = isAuthenticated
                          ? theme.colors.border.hover
                          : theme.colors.border.subtle;
                      }}
                    >
                      <UserIcon />
                    </button>

                    {/* User Card Popup */}
                    {showUserCard && isAuthenticated && user && (
                      <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          position: 'absolute',
                          top: '100%',
                          right: 0,
                          marginTop: theme.spacing['2'],
                          minWidth: '220px',
                          backgroundColor: theme.colors.background.elevated,
                          border: `${theme.borderWidth.base} solid ${theme.colors.border.subtle}`,
                          borderRadius: theme.borderRadius.lg,
                          boxShadow: theme.shadow.lg,
                          padding: theme.spacing['3'],
                          zIndex: 1000,
                        }}
                      >
                        {/* User Info */}
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: theme.spacing['3'],
                            paddingBottom: theme.spacing['3'],
                            borderBottom: `${theme.borderWidth.thin} solid ${theme.colors.border.subtle}`,
                            marginBottom: theme.spacing['2'],
                          }}
                        >
                          {/* Avatar */}
                          <div
                            style={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '50%',
                              backgroundColor: theme.colors.palette.primary,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: theme.colors.text.inverse,
                              fontWeight: theme.typography.fontWeight.semibold,
                              fontSize: theme.typography.fontSize.base,
                              overflow: 'hidden',
                            }}
                          >
                            {user.displayPic ? (
                              <img
                                src={mediaBaseUrl ? `${mediaBaseUrl}${user.displayPic}` : user.displayPic}
                                alt={user.fullName}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            ) : (
                              user.fullName?.charAt(0)?.toUpperCase() || 'U'
                            )}
                          </div>
                          {/* Name & Email */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                fontWeight: theme.typography.fontWeight.medium,
                                fontSize: theme.typography.fontSize.sm,
                                color: theme.colors.text.primary,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {user.fullName || 'User'}
                            </div>
                            <div
                              style={{
                                fontSize: theme.typography.fontSize.xs,
                                color: theme.colors.text.secondary,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {user.email}
                            </div>
                          </div>
                        </div>

                        {/* Logout Button */}
                        <button
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLogout();
                          }}
                          style={{
                            width: '100%',
                            padding: `${theme.spacing['2']} ${theme.spacing['3']}`,
                            backgroundColor: 'transparent',
                            border: 'none',
                            borderRadius: theme.borderRadius.md,
                            color: theme.colors.text.primary,
                            fontSize: theme.typography.fontSize.sm,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: theme.spacing['2'],
                            transition: 'background-color 0.15s ease',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = theme.colors.background.hover;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                            <polyline points="16 17 21 12 16 7" />
                            <line x1="21" y1="12" x2="9" y2="12" />
                          </svg>
                          Sign out
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Collapse Button (minimize) */}
            {newChat && (
              <button
                type="button"
                onClick={newChat}
                title="Start a new chat"
                aria-label="Start a new chat"
                style={{
                  height: '32px',
                  padding: '0 10px',
                  borderRadius: theme.borderRadius.full,
                  border: `${theme.borderWidth.thin} solid ${theme.colors.border.strong}`,
                  backgroundColor: 'transparent',
                  color: theme.colors.text.secondary,
                  fontFamily: 'inherit',
                  fontSize: theme.typography.fontSize.xs,
                  fontWeight: theme.typography.fontWeight.medium,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = theme.colors.background.hover;
                  e.currentTarget.style.color = theme.colors.text.primary;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = theme.colors.text.secondary;
                }}
              >
                <NewChatIcon />
                New chat
              </button>
            )}
            {onCollapse && (
              <button
                onClick={onCollapse}
                title="Minimize"
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: theme.borderRadius.md,
                  border: 'none',
                  backgroundColor: 'transparent',
                  color: theme.colors.text.secondary,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = theme.colors.background.hover;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <CollapseIcon />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Second Row: Agent Selector - only when there are multiple brains to switch
          between (a single brain's identity already shows in the header above). */}
      {!headless && agents && agents.length > 1 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '8px 12px',
            backgroundColor: theme.colors.background.surface,
            flexShrink: 0,
          }}
        >
          <AgentSelectorDropdown
            agents={agents}
            selectedAgent={selectedAgent || null}
            onSelect={(agent) => onAgentChange?.(agent)}
            mediaBaseUrl={mediaBaseUrl}
          />
        </div>
      )}

      {/* Main Content */}
      <EmbedChatActionsProvider onNewChatChange={offerNewChat}>
        <AppContent key={contentKey} wantsToSignIn={wantsToSignIn} onCancelSignIn={handleCancelSignIn} />
      </EmbedChatActionsProvider>
    </div>
  );
};

// ============================================================================
// Themed App Container (uses theme context)
// ============================================================================

interface ThemedAppContainerProps {
  className?: string;
  style?: React.CSSProperties;
  onSessionExpired: () => void;
  onAuthChange?: (isAuthenticated: boolean) => void;
  children?: React.ReactNode;
}

const ThemedAppContainer: React.FC<ThemedAppContainerProps> = ({
  className = '',
  style,
  onSessionExpired,
  onAuthChange,
  children,
}) => {
  const { theme, styles, utils } = useEmbedTheme();
  const { appBaseUrl } = useEmbedConfig();

  const containerStyles: React.CSSProperties = useMemo(
    () =>
      utils.merge(styles.page(), {
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
        height: '100%',
        minHeight: 0,
        flex: 1,
        ...style,
      }),
    [styles, utils, style],
  );

  return (
    <div className={`bolder-embed-app ${className}`} style={containerStyles}>
      {/* Global styles from theme */}
      <style>{utils.globalStyles}</style>

      <EmbedAuthProvider
        appBaseUrl={appBaseUrl || ''}
        onSessionExpired={onSessionExpired}
        onAuthChange={onAuthChange}
      >
        <EmbedOwnerProfileProvider>
          <MainLayout />
        </EmbedOwnerProfileProvider>
      </EmbedAuthProvider>

      {/* Session Expired Notification (rendered via children) */}
      {children}
    </div>
  );
};

export default EmbedApp;
